import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const [items, setItems] = useState([]);
  const [authed, setAuthed] = useState(false);

  const refresh = useCallback(() => {
    api
      .get("/account/wishlist")
      .then(({ data }) => {
        setItems(data);
        setAuthed(true);
      })
      .catch(() => {
        setItems([]);
        setAuthed(false);
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const ids = useMemo(() => new Set(items.map((p) => p.id)), [items]);

  const toggle = useCallback(
    async (product) => {
      if (!authed) {
        toast("Join the Locka Circle to save pieces", { description: "Your wishlist lives on your account page." });
        return;
      }
      if (ids.has(product.id)) {
        setItems((prev) => prev.filter((p) => p.id !== product.id));
        api.delete(`/account/wishlist/${product.id}`).catch(() => {});
        toast.success(`${product.name} removed`, { description: "Lifted out of your wishlist." });
      } else {
        setItems((prev) => [...prev, product]);
        api.post("/account/wishlist", { product_id: product.id }).catch(() => {});
        toast.success(`${product.name} saved`, { description: "Tucked into your wishlist." });
      }
    },
    [authed, ids]
  );

  const value = useMemo(() => ({ items, ids, toggle, authed, refresh }), [items, ids, toggle, authed, refresh]);
  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export const useWishlist = () => useContext(WishlistContext);
